// STORE FILE FOR METHODS
import { DateTime } from "luxon";

export default {
  getMarkerColor(catches) {
    // Fetch base site url
    const baseUrl = window.location.origin;

    // check if there is data
    if (!catches || (catches.count_total == null && String(catches.latest_action_type) !== '6')) {
      return baseUrl + "/img/marker-icon-gray.png";
    }

    let color = "";

    if (String(catches.latest_action_type) === '6') {
      return baseUrl + "/img/marker-icon-brown.png";
    }

    // 100+ --> color: red
    if (catches.count_total >= 100) {
      color = "red";
    }
    // 0-100 --> color: orange
    else if (catches.count_total < 100 && catches.count_total > 0) {
      color = "orange";
    }
    // 0 --> color: green
    else {
      color = "green";
    }

    return baseUrl + `/img/marker-icon-${color}.png`;
  },

  // group ponds per region
  groupByRegion(ponds) {
    const groups = ponds.reduce((groups, pond) => {
      const pondRegion = (pond.region && String(pond.region).trim() !== '') ? String(pond.region).trim() : 'Onbekende Regio';
      const region = Object.keys(groups).find(
        (key) => key.toLowerCase() === pondRegion.toLowerCase(),
      );
      if (!region) {
        groups[pondRegion] = [];
      }
      groups[region || pondRegion].push(pond);
      return groups;
    }, {});

    // sort regions alphabetically in a case-insensitive manner
    const sortedRegions = Object.keys(groups).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" }),
    );

    // sort ponds within each region by name
    for (const region of sortedRegions) {
      groups[region].sort((a, b) =>
        String(a.name || "").localeCompare(String(b.name || ""), undefined, {
          sensitivity: "base",
        }),
      );
    }

    return sortedRegions.reduce((result, region) => {
      result[region] = groups[region];
      return result;
    }, {});
  },

  formatDateTime(datetime_yyyymmddhhiiss) {
    return DateTime.fromISO(datetime_yyyymmddhhiiss.replace(" ", "T"))
      .setLocale("nl-be")
      .toFormat("ccc D T");
  },

  formatDate(datetime_yyyymmdd) {
    return DateTime.fromISO(datetime_yyyymmdd).setLocale("nl-be").toFormat("D");
  },

  formatDateLong(datetime_yyyymmdd) {
    return DateTime.fromISO(datetime_yyyymmdd)
      .setLocale("nl-be")
      .toFormat("ccc D");
  },

  getLatestCatchesTotal(pond) {
    let color = "";
    const catches = pond.includes?.["latest-catches"] ?? {};
    let total = catches["count_total"];

    // check if there is session data
    if (catches.latest_catch_date == null || total == null) {
      total = "-";
      color = "dark";
    } else if (total >= 100) {
      color = "danger";
    }
    // 0-100 --> color: orange
    else if (total < 100 && total > 0) {
      color = "warning";
    }
    // 0 --> color: green
    else if (total === 0) {
      color = "success";
    }
    return `<i class="bi bi-circle-fill text-${color}"></i> ${total}`;
  },

  // Nieuwe helper: toon w_name indien aanwezig, anders name. Als beide bestaan en verschillen, toon beide.
  pondDisplayName(pond) {
    if (!pond) return "";
    const w = pond.w_name ? `${pond.w_name}` : "NO WVLC";
    const n = pond.name;
    const part = pond.pond_part ? `${pond.pond_part}` : "";
    if (w && w !== n && n) {
      return `${n}${part} - ${w}`;
    }
    return w || n || "";
  },
  // Volledig label met regio en deelvijver
  pondFullLabel(pond) {
    if (!pond) return "";
    const base = this.pondDisplayName(pond);

    return `${pond.region ? pond.region + " " : ""}${base}`;
  },

  getParameterLabel(parameters, type, value) {
    if (!parameters || value === null || value === undefined || value === "") {
      return "";
    }
    const list = Array.isArray(parameters[type]) ? parameters[type] : [];
    const valueStr = String(value);
    const match = list.find((item) => String(item.value) === valueStr);
    return match ? match.label : valueStr;
  },

  getSessionClientLabel(clientValue, parameters) {
    return this.getParameterLabel(parameters, "session_client", clientValue);
  },

  // Time tracking helpers
  calculateSessionDuration(startTime, endTime) {
    if (!startTime || !endTime) return 0;

    // Parse HH:MM:SS format
    const [startH, startM, startS] = startTime.split(":").map(Number);
    const [endH, endM, endS] = endTime.split(":").map(Number);

    const startMinutes = startH * 60 + startM + startS / 60;
    let endMinutes = endH * 60 + endM + endS / 60;

    // Handle sessions that span midnight by rolling end time to next day
    if (endMinutes < startMinutes) {
      endMinutes += 24 * 60;
    }
    return (endMinutes - startMinutes) / 60; // Return hours as decimal
  },

  getPondName(pondId, pondsLookup) {
    const pond = pondsLookup[`pond_${pondId}`];
    if (!pond) return `Pond ${pondId}`;

    // Build display name: "region name pond_part (w_name)"
    const region = pond.region || "";
    const name = pond.name || "";
    const part = pond.pond_part || "";
    const wname = pond.w_name;

    // Construct base: "region name pond_part"
    let display = [region, name + part].filter(Boolean).join(" ");

    // Add w_name in parentheses if it exists
    if (wname) {
      display += ` (${wname})`;
    }

    return display || `Pond ${pondId}`;
  },

  getHourThresholdStatus(totalHours) {
    if (totalHours >= 1900) return "critical";
    if (totalHours >= 1710) return "warning";
    return "ok";
  },

  calculateTimeDuration(startTimeISO, endTimeISO) {
    // Calculate duration in hours between two datetime strings
    // Handles both ISO format (YYYY-MM-DDTHH:mm:ss) and SQL format (YYYY-MM-DD HH:mm:ss)
    if (!startTimeISO || !endTimeISO) return null;
    try {
      // Replace space with T to normalize SQL datetime to ISO format
      const startNormalized = startTimeISO.replace(" ", "T");
      const endNormalized = endTimeISO.replace(" ", "T");

      // Both inputs are UTC (registered_at). Pin the zone so the diff is
      // independent of the host machine's local timezone.
      const start = DateTime.fromISO(startNormalized, { zone: "utc" });
      const end = DateTime.fromISO(endNormalized, { zone: "utc" });
      if (!start.isValid || !end.isValid) return null;
      return end.diff(start, "hours").hours;
    } catch {
      return null;
    }
  },

  formatDuration(hours) {
    if (hours === null || hours === undefined) return "N/A";
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m}min`;
  },

  buildDayTimeline(sessions, pondsLookup, parameters = null) {
    const events = [];
    const sessionData = {}; // Store session metadata for duration calculations

    // First pass: collect all events and session metadata
    sessions.forEach((session) => {
      const pondName = this.getPondName(session.pond_id, pondsLookup);
      const sessionId = parseInt(session.id); // Normalize to number early

      // Initialize session data
      sessionData[sessionId] = {
        pondName,
        clientLabel: this.getSessionClientLabel(session.client, parameters),
        startTime: session.start_time,
        endTime: session.end_time,
        registeredAt: session.registered_at,
        catches: [],
      };

      // Session registered_at (device timestamp, UTC)
      if (session.registered_at) {
        events.push({
          time: session.registered_at,
          zone: "utc",
          type: "session_registered",
          label: "Session started (device)",
          sessionId: sessionId,
        });
      }

      // Session start (planned wall-clock, local)
      if (session.start_time) {
        events.push({
          time: `${session.date}T${session.start_time}`,
          zone: "Europe/Brussels",
          type: "session_start",
          label: `Session start - ${pondName}`,
          sessionId: sessionId,
          pondName,
        });
      }

      // Catches (registered_at, UTC)
      if (session.includes && session.includes.catches) {
        session.includes.catches.forEach((catchRecord) => {
          if (catchRecord.registered_at) {
            sessionData[sessionId].catches.push(catchRecord.registered_at);
            events.push({
              time: catchRecord.registered_at,
              zone: "utc",
              type: "catch",
              label: "Catch logged",
              catchId: catchRecord.id,
              sessionId: sessionId,
            });
          }
        });
      }

      // Session end (planned wall-clock, local)
      if (session.end_time) {
        events.push({
          time: `${session.date}T${session.end_time}`,
          zone: "Europe/Brussels",
          type: "session_end",
          label: "Session end",
          sessionId: sessionId,
        });
      }

      // TODO backend logs created_at in Europe/Brussels local time.
      // Treating as local for now; switch to "utc" once the backend stores UTC.
      if (session.created_at) {
        events.push({
          time: session.created_at,
          zone: "Europe/Brussels",
          type: "session_created",
          label: "Session saved to database",
          sessionId: sessionId,
        });
      }
    });

    // Sort events chronologically by absolute instant, since events carry
    // mixed zones (UTC for device timestamps, local for planned times).
    events.sort((a, b) => {
      const ta = DateTime.fromISO(a.time.replace(" ", "T"), {
        zone: a.zone || "utc",
      }).toMillis();
      const tb = DateTime.fromISO(b.time.replace(" ", "T"), {
        zone: b.zone || "utc",
      }).toMillis();
      return ta - tb;
    });

    // Calculate session durations
    const sessionsWithDurations = Object.entries(sessionData).map(
      ([sessionId, data]) => {
        // Planned duration: start_time to end_time
        const plannedDuration = this.calculateSessionDuration(
          data.startTime,
          data.endTime,
        );

        // Actual duration: registered_at to last catch registered_at
        let actualDuration = null;
        if (data.registeredAt && data.catches.length > 0) {
          const lastCatch = data.catches.sort().reverse()[0];
          actualDuration = this.calculateTimeDuration(
            data.registeredAt,
            lastCatch,
          );
        }

        return {
          sessionId: parseInt(sessionId),
          pondName: data.pondName,
          clientLabel: data.clientLabel,
          plannedDuration,
          actualDuration,
          plannedStart: data.startTime,
          plannedEnd: data.endTime,
          actualStart: data.registeredAt,
          actualEnd:
            data.catches.length > 0 ? data.catches.sort().reverse()[0] : null,
          hasCatches: data.catches.length > 0,
        };
      },
    );

    // Calculate day duration: first session registered_at to last catch registered_at
    let dayDuration = null;
    const allRegisteredStarts = sessions
      .map((s) => s.registered_at)
      .filter(Boolean)
      .sort();
    const allCatches = sessions
      .flatMap((s) => s.includes?.catches || [])
      .map((c) => c.registered_at)
      .filter(Boolean)
      .sort();

    if (allRegisteredStarts.length > 0 && allCatches.length > 0) {
      const firstStart = allRegisteredStarts[0];
      const lastCatch = allCatches[allCatches.length - 1];
      dayDuration = this.calculateTimeDuration(firstStart, lastCatch);
    }

    return {
      events,
      sessions: sessionsWithDurations,
      dayDuration,
      dayStart: allRegisteredStarts[0] || null,
      dayEnd: allCatches[allCatches.length - 1] || null,
      status: dayDuration ? this.getHourThresholdStatus(dayDuration) : "ok",
    };
  },
};
